import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";

export default function DriverManagerSetting({ onOpenOwnProfile }) {
  const navigate = useNavigate();
  const auth = getAuth();

  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);

  const uid = user?.uid || "";

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser || null);
      setAuthChecked(true);
    });

    return () => unsub();
  }, [auth]);

  useEffect(() => {
    if (!authChecked) return;

    if (!uid) {
      setProfile(null);
      setLoadingProfile(false);
      return;
    }

    const ref = doc(db, "drivers", uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) setProfile({ id: snap.id, ...snap.data() });
        else setProfile(null);
        setLoadingProfile(false);
      },
      (err) => {
        console.log("[DriverManagerSetting] profile snapshot error:", err);
        setLoadingProfile(false);
      }
    );

    return () => unsub && unsub();
  }, [authChecked, uid]);

  const displayName = useMemo(() => {
    return String(profile?.displayName || user?.displayName || "成員").trim();
  }, [profile?.displayName, user?.displayName]);

  const phoneText = useMemo(() => {
    const p = String(profile?.phone || user?.phoneNumber || "").trim();
    return p || "電話未設定";
  }, [profile?.phone, user?.phoneNumber]);

  const emailText = useMemo(() => {
    const email = String(profile?.email || user?.email || "").trim();
    return email || "Email 未設定";
  }, [profile?.email, user?.email]);

  const photoURL = useMemo(() => {
    return String(
      profile?.photo?.previewURL ||
        profile?.photoURL ||
        profile?.avatarUrl ||
        user?.photoURL ||
        ""
    ).trim();
  }, [profile?.photo?.previewURL, profile?.photoURL, profile?.avatarUrl, user?.photoURL]);

  const handleLogout = useCallback(async () => {
    const ok = window.confirm("確定要登出目前帳號嗎？");
    if (!ok) return;

    try {
      setLoggingOut(true);
      await signOut(auth);
      navigate("/login", { replace: true });
    } catch (err) {
      console.log("[DriverManagerSetting] signOut error:", err);
      window.alert(err?.message ?? String(err));
    } finally {
      setLoggingOut(false);
    }
  }, [auth, navigate]);

  if (!authChecked) {
    return (
      <div style={styles.centerContainer}>
        <div style={styles.spinnerLarge} />
        <div style={styles.centerSub}>驗證登入狀態中…</div>
      </div>
    );
  }

  if (!uid) {
    return (
      <div style={styles.centerContainer}>
        <div style={styles.centerTitle}>車隊設定</div>
        <div style={styles.centerSub}>請先登入帳號</div>
      </div>
    );
  }

  return (
    <div style={styles.root}>
      <div style={styles.header}>
        <div>
          <div style={styles.headerTitle}>設定</div>
          <div style={styles.headerSubtitle}>管理個人資料與服務資訊</div>
        </div>
      </div>

      <div style={styles.scrollWrap}>
        <div style={styles.contentInner}>
          <section style={{ ...styles.card, ...styles.profileCard }}>
            <div style={styles.profileSectionHead}>
              <div>
                <div style={styles.cardTitle}>個人資料</div>
                <div style={styles.profileSectionHint}>頭貼、姓名與聯絡方式</div>
              </div>
              <button
                type="button"
                style={styles.editProfileButton}
                onClick={() => onOpenOwnProfile?.()}
              >
                編輯資料
                <ChevronRightIcon />
              </button>
            </div>

            <div style={styles.profileIdentity}>
              <div style={styles.avatar}>
                {photoURL ? (
                  <img src={photoURL} alt={`${displayName} 頭貼`} style={styles.avatarImg} />
                ) : (
                  <span style={styles.avatarText}>{displayName?.[0] || "司"}</span>
                )}
              </div>
              <div style={styles.profileIdentityText}>
                <div style={styles.profileName}>
                  {loadingProfile ? "載入中…" : displayName}
                </div>
              </div>
            </div>

            <div style={styles.profileMetaGrid}>
              <div style={styles.profileMetaItem}>
                <div style={styles.profileMetaLabel}>聯絡電話</div>
                <div style={styles.profileMetaValue}>
                  {loadingProfile ? "載入中…" : phoneText}
                </div>
              </div>
              <div style={styles.profileMetaItem}>
                <div style={styles.profileMetaLabel}>Email</div>
                <div style={styles.profileMetaValue}>
                  {loadingProfile ? "載入中…" : emailText}
                </div>
              </div>
            </div>
          </section>

          <div style={styles.card}>
            <Row
              icon={<InfoIcon />}
              title="關於本服務"
              sub="Tallarge 成員與車隊協作平台"
              onPress={() =>
                window.alert("Tallarge 提供成員、車隊與運輸工作所需的協作服務。")
              }
              isLast
            />
          </div>

          <div style={styles.card}>
            <button
              type="button"
              style={{ ...styles.listRow, ...styles.rowButton, ...styles.logoutRow }}
              onClick={handleLogout}
              disabled={loggingOut}
            >
              <div style={styles.listRowLeft}>
                <div
                  style={{
                    ...styles.iconCircle,
                    backgroundColor: "#FEF2F2",
                    color: "#DC2626",
                  }}
                >
                  <LogoutIcon />
                </div>
                <div style={{ flex: 1, textAlign: "left" }}>
                  <div style={styles.logoutText}>
                    {loggingOut ? "登出中…" : "登出"}
                  </div>
                  <div style={styles.listRowSub}>登出目前帳號</div>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ icon, title, sub, onPress, isLast = false }) {
  return (
    <button
      type="button"
      style={{
        ...styles.listRow,
        ...styles.rowButton,
        ...(isLast ? { borderBottomWidth: 0 } : {}),
      }}
      onClick={onPress}
    >
      <div style={styles.listRowLeft}>
        <div style={styles.iconCircle}>{icon}</div>
        <div style={{ flex: 1, textAlign: "left" }}>
          <div style={styles.listRowTitle}>{title}</div>
          {!!sub && <div style={styles.listRowSub}>{sub}</div>}
        </div>
      </div>
      <ChevronRightIcon />
    </button>
  );
}

function ChevronRightIcon() {
  return (
    <svg viewBox="0 0 24 24" style={styles.chevronIcon}>
      <path
        d="M9 6l6 6-6 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 24 24" style={styles.rowIcon}>
      <path
        d="M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M12 10.5v4M12 8h.01"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg viewBox="0 0 24 24" style={styles.rowIcon}>
      <path
        d="M15 16.5 19.5 12 15 7.5M19 12H9M11 5H6.5A1.5 1.5 0 0 0 5 6.5v11A1.5 1.5 0 0 0 6.5 19H11"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const styles = {
  root: {
    height: "100vh",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxSizing: "border-box",
    background: "#F3F4F6",
  },
  centerContainer: {
    minHeight: "100%",
    background: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
  },
  centerTitle: {
    fontSize: 18,
    fontWeight: 800,
    color: "#111827",
  },
  centerSub: {
    marginTop: 4,
    color: "#6b7280",
    fontWeight: 600,
  },
  header: {
    flex: "0 0 auto",
    padding: "4px 4px 14px",
    backgroundColor: "#F3F4F6",
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: 900,
    color: "#111827",
    letterSpacing: -0.4,
  },
  headerSubtitle: {
    marginTop: 4,
    fontSize: 13,
    color: "#6b7280",
    fontWeight: 700,
  },
  scrollWrap: {
    width: "100%",
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    overscrollBehavior: "contain",
    WebkitOverflowScrolling: "touch",
  },
  contentInner: {
    display: "grid",
    gap: 14,
    paddingBottom: 20,
  },
  card: {
    backgroundColor: "#fff",
    padding: 16,
    borderRadius: 20,
    border: "1px solid #E5E7EB",
    boxShadow: "0 10px 24px rgba(0,0,0,0.05)",
  },
  profileCard: {
    padding: 18,
  },
  profileSectionHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 16,
  },
  profileSectionHint: {
    marginTop: 3,
    color: "#7B8790",
    fontSize: 12,
    fontWeight: 600,
  },
  editProfileButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    flexShrink: 0,
    minHeight: 34,
    padding: "0 10px",
    border: "1px solid #DCE6EB",
    borderRadius: 10,
    background: "#F8FAFB",
    color: "#426B80",
    fontSize: 12,
    fontWeight: 750,
    cursor: "pointer",
  },
  profileIdentity: {
    display: "grid",
    justifyItems: "center",
    gap: 10,
    minWidth: 0,
    padding: "2px 0",
    textAlign: "center",
  },
  profileIdentityText: {
    display: "grid",
    justifyItems: "center",
    minWidth: 0,
  },
  profileName: {
    overflow: "hidden",
    color: "#152D3C",
    fontSize: 22,
    fontWeight: 900,
    letterSpacing: -0.3,
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textAlign: "center",
  },
  profileMetaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
    marginTop: 16,
    paddingTop: 14,
    borderTop: "1px solid #EDF1F3",
  },
  profileMetaItem: {
    minWidth: 0,
    padding: 11,
    border: "1px solid #EDF1F3",
    borderRadius: 12,
    background: "#F9FBFC",
  },
  profileMetaLabel: {
    color: "#82919A",
    fontSize: 11,
    fontWeight: 700,
  },
  profileMetaValue: {
    overflow: "hidden",
    marginTop: 5,
    color: "#293F4B",
    fontSize: 13,
    fontWeight: 750,
    lineHeight: 1.4,
    overflowWrap: "anywhere",
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: 800,
    color: "#111827",
    marginBottom: 8,
  },
  listRow: {
    display: "flex",
    alignItems: "center",
    padding: "12px 0",
    borderBottom: "1px solid #F3F4F6",
  },
  rowButton: {
    width: "100%",
    background: "transparent",
    borderLeft: "none",
    borderRight: "none",
    borderTop: "none",
    cursor: "pointer",
  },
  listRowLeft: {
    display: "flex",
    alignItems: "center",
    flex: 1,
    gap: 10,
    minWidth: 0,
  },
  listRowTitle: {
    fontWeight: 800,
    color: "#111827",
    fontSize: 14,
  },
  listRowSub: {
    color: "#6b7280",
    fontSize: 11,
    marginTop: 2,
    lineHeight: 1.5,
  },
  avatar: {
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: "#E7F0F5",
    border: "3px solid #fff",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
    flexShrink: 0,
    boxShadow: "0 5px 16px rgba(43,75,91,0.14)",
  },
  avatarText: {
    color: "#315A73",
    fontSize: 25,
    fontWeight: 900,
  },
  avatarImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "50%",
  },
  iconCircle: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: "#F3F4F6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#111827",
    flexShrink: 0,
  },
  rowIcon: {
    width: 18,
    height: 18,
    display: "block",
  },
  chevronIcon: {
    width: 16,
    height: 16,
    color: "#9CA3AF",
    flexShrink: 0,
  },
  logoutText: {
    fontWeight: 900,
    color: "#DC2626",
    fontSize: 14,
  },
  logoutRow: {
    marginTop: 4,
    borderBottom: "none",
  },
  spinnerLarge: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    border: "2px solid #D1D5DB",
    borderTopColor: "#111827",
    animation: "driverSettingSpin 1s linear infinite",
    marginBottom: 10,
  },
};

if (typeof document !== "undefined" && !document.getElementById("driver-setting-spin-style")) {
  const style = document.createElement("style");
  style.id = "driver-setting-spin-style";
  style.innerHTML = `
    @keyframes driverSettingSpin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);
}
